import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, Share2, Eye } from 'lucide-react';
import DOMPurify from 'dompurify';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import OptimizedImage from '@/components/OptimizedImage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Comprar': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Vender': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Rentar': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Mercado Guatemala': 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/articles/${slug}`);
      if (!response.ok) throw new Error('Article not found');
      
      const data = await response.json();
      setArticle(data);
      
      // Fetch related articles from same category
      const relatedResponse = await fetch(
        `${API_URL}/api/blog/articles?category=${data.category}&limit=3`
      );
      const relatedData = await relatedResponse.json();
      setRelatedArticles(
        relatedData.articles?.filter(a => a.id !== data.id).slice(0, 2) || []
      );
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-GT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const wordCount = content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min de lectura`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = article?.title || 'Artículo de Ikonic Deals';
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  // Schema markup for article
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description,
    "image": article.cover_image,
    "author": {
      "@type": "Organization",
      "name": article.author || "Ikonic Deals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Ikonic Deals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ikonicdeals.com/logo.png"
      }
    },
    "datePublished": article.published_at,
    "dateModified": article.updated_at
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="pt-32 pb-16">
          <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
            <Skeleton className="h-8 w-32 bg-[#1E1E1E] mb-6" />
            <Skeleton className="h-12 w-full bg-[#1E1E1E] mb-4" />
            <Skeleton className="h-6 w-48 bg-[#1E1E1E] mb-8" />
            <Skeleton className="aspect-[16/9] bg-[#1E1E1E] rounded-xl mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full bg-[#1E1E1E]" />
              <Skeleton className="h-4 w-full bg-[#1E1E1E]" />
              <Skeleton className="h-4 w-3/4 bg-[#1E1E1E]" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-2xl text-white mb-4">Artículo no encontrado</h1>
          <Link to="/blog">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]">
              <ArrowLeft className="mr-2" size={16} />
              Volver al blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="blog-article-page">
      <SEO 
        title={`${article.title} | Blog Ikonic Deals`}
        description={article.meta_description}
        keywords={`${article.category}, blog inmobiliario guatemala, ${article.title.toLowerCase()}`}
        url={`/blog/${article.slug}`}
        image={article.cover_image}
        schema={articleSchema}
      />
      <Header />
      
      {/* Article Header */}
      <section className="pt-32 pb-8">
        <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
          {/* Back Button */}
          <Link to="/blog" className="inline-flex items-center text-[#A3A3A3] hover:text-white mb-6 transition-colors">
            <ArrowLeft size={16} className="mr-2" />
            Volver al blog
          </Link>

          {/* Category & Meta */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap items-center gap-3 mb-4"
          >
            <span className={`px-3 py-1 text-sm rounded-full border ${CATEGORY_COLORS[article.category] || CATEGORY_COLORS['Comprar']}`}>
              <Tag size={12} className="inline mr-1" />
              {article.category}
            </span>
            <span className="text-[#666] text-sm flex items-center gap-1">
              <Calendar size={14} />
              {formatDate(article.published_at)}
            </span>
            <span className="text-[#666] text-sm flex items-center gap-1">
              <Clock size={14} />
              {estimateReadTime(article.content)}
            </span>
            <span className="text-[#666] text-sm flex items-center gap-1">
              <Eye size={14} />
              {article.views || 0} vistas
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl text-white font-medium mb-6"
          >
            {article.title}
          </motion.h1>

          {/* Author & Share */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-between border-b border-white/10 pb-6 mb-8"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-medium">
                {article.author?.charAt(0) || 'I'}
              </div>
              <div>
                <p className="text-white text-sm">{article.author || 'Ikonic Deals'}</p>
                <p className="text-[#666] text-xs">Autor</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Share2 size={14} className="mr-2" />
              Compartir
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Cover Image */}
      {article.cover_image && (
        <section className="pb-8">
          <div className="container-custom px-4 sm:px-6 max-w-5xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="aspect-[16/9] rounded-2xl overflow-hidden"
            >
              <OptimizedImage
                src={article.cover_image}
                alt={article.title}
                className="w-full h-full"
                priority={true}
              />
            </motion.div>
          </div>
        </section>
      )}

      {/* Article Content */}
      <section className="py-8">
        <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-medium
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-[#CCCCCC] prose-p:leading-relaxed
              prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-ul:text-[#CCCCCC] prose-ol:text-[#CCCCCC]
              prose-li:marker:text-[#D4AF37]
              prose-blockquote:border-l-[#D4AF37] prose-blockquote:text-[#A3A3A3]
              prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            data-testid="article-content"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-2xl p-8 text-center">
            <h3 className="text-2xl text-white font-medium mb-3">
              ¿Listo para encontrar tu propiedad ideal?
            </h3>
            <p className="text-[#A3A3A3] mb-6">
              Nuestro equipo de expertos está listo para ayudarte
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/propiedades">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#FFE082]">
                  Ver Propiedades
                </Button>
              </Link>
              <Link to="/contacto">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  Contactar Asesor
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 border-t border-white/10">
          <div className="container-custom px-4 sm:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl text-white font-medium mb-8">Artículos Relacionados</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedArticles.map((related) => (
                <Link 
                  key={related.id} 
                  to={`/blog/${related.slug}`}
                  className="group flex gap-4 p-4 bg-[#121212] border border-white/5 rounded-xl hover:border-[#D4AF37]/30 transition-all"
                >
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-[#1a1a1a]">
                    {related.cover_image ? (
                      <OptimizedImage
                        src={related.cover_image}
                        alt={related.title}
                        className="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <span className="text-[#D4AF37]/30">ID</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full border mb-2 ${CATEGORY_COLORS[related.category]}`}>
                      {related.category}
                    </span>
                    <h3 className="text-white text-sm font-medium group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-[#666] text-xs mt-1">
                      {formatDate(related.published_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
